import React, { useState, useEffect, useCallback } from 'react';
import { validateField } from '../utils/validation';

const ValidatedInput = ({
  type = 'text',
  value,
  onChange,
  onBlur,
  validationType,
  customRules = {},
  required = false,
  placeholder = '',
  className = '',
  label = '',
  disabled = false,
  showErrors = true,
  tooltip = '',
  ...props
}) => {
  const [errors, setErrors] = useState([]);
  const [touched, setTouched] = useState(false);

  // Memoize validation function to prevent unnecessary re-renders
  const validateCurrentField = useCallback(() => {
    if (validationType) {
      return validateField(value, validationType, customRules);
    }
    return [];
  }, [value, validationType, JSON.stringify(customRules)]);

  // Validate field when value changes (if already touched)
  useEffect(() => {
    if (touched) {
      const fieldErrors = validateCurrentField();
      setErrors(fieldErrors);
    }
  }, [touched, validateCurrentField]);

  const handleBlur = (e) => {
    setTouched(true);
    if (validationType) {
      const fieldErrors = validateField(value, validationType, customRules);
      setErrors(fieldErrors);
    }
    if (onBlur) onBlur(e);
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(e);

    // Clear errors when user starts typing (if field becomes valid)
    if (touched && validationType) {
      const fieldErrors = validateField(newValue, validationType, customRules);
      setErrors(fieldErrors);
    }
  };

  // Prevent arrow key auto-increment/decrement for number inputs
  const handleKeyDown = (e) => {
    if (type === 'number' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
    }
  };

  const handleWheel = (e) => {
    if (type === 'number') {
      e.target.blur();
    }
  };

  const hasErrors = touched && errors.length > 0;
  const inputClassName = `
    ${className}
    ${hasErrors ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-[#C5A57D] focus:ring-[#C5A57D]'}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
  `.trim();

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
          {tooltip && (
            <div className="group relative inline-block ml-1.5 align-middle">
              <span className="text-gray-400 cursor-help hover:text-[#0A5F38] transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <div className="absolute z-[9999] bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-slate-900 text-white text-[11px] leading-relaxed rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform scale-95 group-hover:scale-100">
                <div className="relative">
                  {tooltip}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 border-8 border-transparent border-t-slate-900"></div>
                </div>
              </div>
            </div>
          )}
        </label>
      )}

      <input
        type={type}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onWheel={handleWheel}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${inputClassName} ${type === 'number' ? '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' : ''}`}
        {...props}
      />

      {showErrors && hasErrors && (
        <div className="mt-1">
          {errors.map((error, index) => (
            <p key={index} className="text-sm text-red-600 flex items-center">
              <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default ValidatedInput;

